/**
 * Step 1: Method Selection
 *
 * Select import method: Single CV or Bulk CV Import
 */

import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
} from "@mui/material";
import { UploadFile, DriveFolderUpload } from "@mui/icons-material";

interface Props {
  onNext: () => void;
  updateState: (
    updates: Partial<import("./CandidateImportWizard").WizardState>,
  ) => void;
}

const Step1_MethodSelection: React.FC<Props> = ({ onNext, updateState }) => {
  const { t } = useTranslation(["recruiting"]);
  const handleSelectSingle = () => {
    updateState({ importMethod: "single-cv" });
    onNext();
  };

  const handleSelectBulk = () => {
    updateState({ importMethod: "bulk-cv" });
    onNext();
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {t("importWizard.step1.title")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t("importWizard.step1.subtitle")}
      </Typography>

      <Box sx={{ display: "flex", gap: 3 }}>
        <Card sx={{ maxWidth: 400, flex: 1 }}>
          <CardActionArea
            onClick={handleSelectSingle}
            data-testid="method-single-cv"
            sx={{ transition: "all 0.2s", height: "100%" }}
          >
            <CardContent sx={{ textAlign: "center", py: 4 }}>
              <UploadFile sx={{ fontSize: 64, color: "primary.main", mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {t("importWizard.step1.singleTitle")}
              </Typography>
              <Typography color="text.secondary">
                {t("importWizard.step1.singleDescription")}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>

        <Card sx={{ maxWidth: 400, flex: 1 }}>
          <CardActionArea
            onClick={handleSelectBulk}
            data-testid="method-bulk-cv"
            sx={{ transition: "all 0.2s", height: "100%" }}
          >
            <CardContent sx={{ textAlign: "center", py: 4 }}>
              <DriveFolderUpload
                sx={{ fontSize: 64, color: "primary.main", mb: 2 }}
              />
              <Typography variant="h6" gutterBottom>
                {t("importWizard.step1.bulkTitle")}
              </Typography>
              <Typography color="text.secondary">
                {t("importWizard.step1.bulkDescription")}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 2, display: "block" }}
      >
        {t("importWizard.step1.futureHint")}
      </Typography>
    </Box>
  );
};

export default Step1_MethodSelection;
